import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { consultasService, pacientesService } from '../services/api';
import { consultasRecorrentesService } from '../services/api/consultasRecorrentesService';
import { authService } from '../services/authService';
import type { Consulta, ConsultaFormState, ConsultaInput } from '../types';
import type { Paciente } from '../services/api/pacientesService';
import { DIAS_SEMANA, calcularDatasRecorrentesCompleta } from '../utils/date-utils';
import FiltroPsicologo from '../components/FiltroPsicologo';
import PacienteSearchSelect from '../components/PacienteSearchSelect';

// Função para normalizar horário do formato "HH:mm:ss" para "HH:mm"
const normalizarHorario = (horario: string | null | undefined): string => {
  if (!horario) return '';
  
  // Se já estiver no formato "HH:mm", retornar como está
  if (/^\d{2}:\d{2}$/.test(horario)) {
    return horario;
  }
  
  // Se estiver no formato "HH:mm:ss", remover os segundos
  if (/^\d{2}:\d{2}:\d{2}$/.test(horario)) {
    return horario.substring(0, 5); // Retorna apenas "HH:mm"
  }
  
  // Caso contrário, tentar extrair HH:mm
  const match = horario.match(/^(\d{2}):(\d{2})/);
  if (match) {
    return `${match[1]}:${match[2]}`;
  }
  
  return '';
};

// Gerar lista de horários para seleção
const gerarHorarios = (horaMinima?: string): string[] => {
  const horarios: string[] = [];
  for (let hora = 6; hora < 23; hora++) {
    for (let minuto = 0; minuto < 60; minuto += 15) {
      const horario = `${hora.toString().padStart(2, '0')}:${minuto.toString().padStart(2, '0')}`;
      if (!horaMinima || horario > horaMinima) {
        horarios.push(horario);
      }
    }
  }
  return horarios;
};

export default function FormConsulta() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [validando, setValidando] = useState(false);
  const [todosPacientes, setPacientes] = useState<Paciente[]>([]);
  const [pacienteSelecionado, setPacienteSelecionado] = useState<Paciente | null>(null);
  const [erros, setErros] = useState<Record<string, string>>({});
  
  // Estado de validação de agendamento
  const [validacaoAgendamento, setValidacaoAgendamento] = useState<{
    valido: boolean;
    tipo?: 'conflito' | 'indisponivel';
    mensagem?: string;
    consultaConflitante?: {
      id: string;
      pacienteNome: string;
      horaInicio: string;
      horaFim: string | null;
    };
    detalhes?: any;
  } | null>(null);
  
  // Estado simplificado do formulário
  const [formData, setFormData] = useState<ConsultaFormState>({
    id: '',
    pacienteId: '',
    usuarioId: '',
    data: '',
    hora: '',
    horaFim: '',
    valor: '',
    statusConsulta: 'agendada',
    statusPagamento: 'pendente',
    dataPagamento: '',
    formaPagamento: undefined,
    observacoes: '',
    realizada: false,
    pagamentoRecebido: false,
    dataConsulta: '',
    horaInicio: '',
    modoRecorrente: false,
    diaDaSemana: 1,
    quantidadeSemanas: 4,
    valorCalculado: 0,
    observacoesAgendamento: '',
    dataInicio: '',
    conflitosDetectados: [],
    validacaoErros: {}
  });

  // Lista de horários filtrados para hora fim (apenas posterior à hora início)
  const horariosDisponiveis = useMemo(() => gerarHorarios(), []);
  const horariosFimDisponiveis = useMemo(() => {
    if (!formData.hora) return horariosDisponiveis;
    return gerarHorarios(formData.hora);
  }, [formData.hora, horariosDisponiveis]);

  // Estados específicos para consultas recorrentes
  const [modoRecorrente, setModoRecorrente] = useState(false);
  const [datasRecorrentes, setDatasRecorrentes] = useState<string[]>([]);

  // Carregar parâmetros da URL (se houver)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const dataParam = params.get('data');
    const horaParam = params.get('hora');
    const pacienteIdParam = params.get('pacienteId');
    
    // CORREÇÃO: Construir atualizações com todas as propriedades necessárias
    const updates: Partial<ConsultaFormState> = {};
    
    if (dataParam) {
      updates.data = dataParam;
      updates.dataConsulta = dataParam; // IMPORTANTE: Manter campos sincronizados
    }
    
    if (horaParam) {
      updates.hora = horaParam;
      updates.horaInicio = horaParam; // IMPORTANTE: Manter campos sincronizados
    }
    
    if (pacienteIdParam) {
      updates.pacienteId = pacienteIdParam;
      
      // Buscar dados do paciente e pré-selecionar
      const pacienteEncontrado = todosPacientes.find(p => p.id === pacienteIdParam);
      if (pacienteEncontrado) {
        setPacienteSelecionado(pacienteEncontrado);
      }
    }
    
    if (Object.keys(updates).length > 0) {
      setFormData(prev => ({ ...prev, ...updates }));
    }
  }, [location.search, todosPacientes]);
  
  // Carregar lista de pacientes
  useEffect(() => {
    async function carregarPacientes() {
      try {
        setCarregando(true);
        const data = await pacientesService.listarTodos();
        
        if (!Array.isArray(data)) {
          console.error('Erro: pacienteService.listarTodos() não retornou array');
          setPacientes([]);
          return;
        }
        
        setPacientes(data);
      } catch (error) {
        console.error('Erro ao carregar pacientes:', error);
        setPacientes([]);
      } finally {
        setCarregando(false);
      }
    }
    
    carregarPacientes();
  }, []);
  
  // Validar agendamento quando usuário, data ou horário mudar
  useEffect(() => {
    const validarAgendamento = async () => {
      // Só validar se tiver todos os dados necessários
      if (!formData.usuarioId || !formData.data || !formData.hora) {
        setValidacaoAgendamento(null);
        return;
      }

      setValidando(true);
      try {
        const resultado = await consultasService.validarAgendamento({
          usuarioId: formData.usuarioId,
          dataConsulta: formData.data,
          horaInicio: formData.hora,
          horaFim: formData.horaFim || undefined,
          consultaIdExcluir: id // Excluir a própria consulta na edição
        });

        if (resultado.sucesso) {
          setValidacaoAgendamento({
            valido: resultado.valido,
            tipo: resultado.tipo,
            mensagem: resultado.mensagem,
            consultaConflitante: resultado.consultaConflitante,
            detalhes: resultado.detalhes
          });
        }
      } catch (error) {
        console.error('[FormConsulta] Erro na validação:', error);
      } finally {
        setValidando(false);
      }
    };

    // Debounce de 300ms para não validar a cada tecla
    const timeoutId = setTimeout(validarAgendamento, 300);
    return () => clearTimeout(timeoutId);
  }, [formData.usuarioId, formData.data, formData.hora, formData.horaFim, id]);
  
  // Carregar consulta para edição - CORRIGIDO
  useEffect(() => {
    async function carregarConsulta() {
      if (!id) return;
      
      try {
        setCarregando(true);
        const consulta = await consultasService.obterPorId(id);
        
        if (consulta) {
          // Mapear TODOS os campos do backend para o form
          setFormData({
            id: consulta.id,
            pacienteId: consulta.pacienteId,
            usuarioId: consulta.usuarioId,
            
            // Frontend fields - NORMALIZAR HORÁRIOS
            data: consulta.dataConsulta || '',
            hora: normalizarHorario(consulta.horaInicio),
            horaFim: normalizarHorario(consulta.horaFim),
            valor: consulta.valor,
            
            // Backend fields - IMPORTANTE! Normalizar horários também
            dataConsulta: consulta.dataConsulta || '',
            horaInicio: normalizarHorario(consulta.horaInicio),
            
            statusConsulta: consulta.statusConsulta,
            statusPagamento: consulta.statusPagamento,
            dataPagamento: consulta.dataPagamento || '',
            formaPagamento: consulta.formaPagamento,
            observacoes: consulta.observacoesAgendamento || '',
            
            // Legacy computed fields
            realizada: consulta.statusConsulta === 'realizada',
            pagamentoRecebido: consulta.statusPagamento === 'pago',
            
            // Required fields for ConsultaFormState
            modoRecorrente: false,
            diaDaSemana: 1,
            quantidadeSemanas: 4,
            valorCalculado: 0,
            observacoesAgendamento: consulta.observacoesAgendamento || '',
            // CORREÇÃO: Incluir dataInicio também no carregamento de dados
            dataInicio: '',
            conflitosDetectados: [],
            validacaoErros: {}
          });
          
          console.log('Dados da consulta carregados:', consulta);
          
          // Carregar dados do paciente selecionado
          if (consulta.pacienteId) {
            const paciente = await pacientesService.obterPorId(consulta.pacienteId);
            setPacienteSelecionado(paciente);
          }
        } else {
          // Handle case when consulta is null
          console.error('Consulta não encontrada');
          alert('Consulta não encontrada');
          navigate('/consultas');
        }
      } catch (error) {
        console.error('Erro ao carregar consulta:', error);
        alert('Não foi possível carregar os dados da consulta.');
        navigate('/consultas');
      } finally {
        setCarregando(false);
      }
    }
    
    carregarConsulta();
  }, [id, navigate]);
  
  // Atualizar paciente selecionado quando mudar o ID
  useEffect(() => {
    if (formData.pacienteId) {
      const paciente = todosPacientes.find((p: Paciente) => p.id === formData.pacienteId);
      if (paciente) {
        setPacienteSelecionado(paciente);
      }
    } else {
      setPacienteSelecionado(null);
    }
  }, [formData.pacienteId, todosPacientes]);

  // Handler para seleção de paciente via componente unificado
  const handlePacienteChange = (pacienteId: string, paciente: Paciente | null) => {
    setFormData(prev => ({ ...prev, pacienteId }));
    setPacienteSelecionado(paciente);
    if (erros.pacienteId) {
      setErros(prev => ({ ...prev, pacienteId: '' }));
    }
  };
  
  // Função para calcular e atualizar datas recorrentes
  const atualizarDatasRecorrentes = () => {
    if (!modoRecorrente || !formData.data || !formData.diaDaSemana || !formData.quantidadeSemanas) {
      setDatasRecorrentes([]);
      return;
    }

    const datas = calcularDatasRecorrentesCompleta(
      formData.data,
      formData.diaDaSemana,
      formData.quantidadeSemanas
    );
    
    setDatasRecorrentes(datas);
    console.log('[FormConsulta] Datas recorrentes calculadas:', datas);
  };

  // Atualizar datas recorrentes quando parâmetros mudarem
  useEffect(() => {
    atualizarDatasRecorrentes();
  }, [modoRecorrente, formData.data, formData.diaDaSemana, formData.quantidadeSemanas]);

  // Função para lidar com mudanças no formulário
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (erros[name]) {
      setErros(prev => ({ ...prev, [name]: '' }));
    }
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev: ConsultaFormState) => ({ ...prev, [name]: checked }));
    } else if (name === 'valor') {
      const inputValue = value === '' ? '' : parseFloat(value);
      setFormData((prev: ConsultaFormState) => ({ ...prev, [name]: inputValue as any }));
    } else if (name === 'data') {
      setFormData((prev: ConsultaFormState) => ({ 
        ...prev, 
        data: value, 
        dataConsulta: value,
        dataInicio: value 
      }));
    } else if (name === 'hora') {
      setFormData((prev: ConsultaFormState) => ({ 
        ...prev, 
        hora: value, 
        horaInicio: value 
      }));
    } else if (name === 'diaDaSemana') {
      setFormData((prev: ConsultaFormState) => ({ 
        ...prev, 
        diaDaSemana: parseInt(value) 
      }));
    } else if (name === 'quantidadeSemanas') {
      setFormData((prev: ConsultaFormState) => ({ 
        ...prev, 
        quantidadeSemanas: parseInt(value) 
      }));
    } else {
      setFormData((prev: ConsultaFormState) => ({ ...prev, [name]: value }));
    }
  };
  
  // Função para criar múltiplas consultas recorrentes
  const criarConsultasRecorrentes = async () => {
    try {
      setSalvando(true);
      
      if (datasRecorrentes.length === 0) {
        alert('Não há datas calculadas para criar consultas recorrentes.');
        return;
      }
      
      // CORREÇÃO: Garantir que o usuário logado e seu ID estejam disponíveis
      const usuarioAtual = authService.getUsuario();
      if (!usuarioAtual || !usuarioAtual.id) {
        alert('Erro: Usuário não autenticado. Faça login novamente.');
        navigate('/login');
        return;
      }
      
      // CORREÇÃO: Garantir que o valor é um número válido
      const valorNumerico = typeof formData.valor === 'string' 
        ? parseFloat(formData.valor) 
        : formData.valor;
        
      if (isNaN(valorNumerico) || valorNumerico <= 0) {
        alert('O valor da consulta deve ser um número válido');
        return;
      }
      
      console.log('[FormConsulta] Enviando dados para API:', {
        pacienteId: formData.pacienteId,
        horaInicio: formData.hora,
        valor: valorNumerico,
        usuarioId: usuarioAtual.id
      });
      
      // CORREÇÃO: Usar o serviço centralizado
      const resultado = await consultasRecorrentesService.agendarConsultasRecorrentes(
        {
          pacienteId: String(formData.pacienteId),
          horaInicio: formData.hora,
          horaFim: formData.horaFim || undefined,
          valor: valorNumerico,
          usuarioId: String(usuarioAtual.id),
          observacoesAgendamento: formData.observacoesAgendamento || `Consulta recorrente - ${DIAS_SEMANA[formData.diaDaSemana]}`
        },
        {
          diaDaSemana: formData.diaDaSemana,
          dataInicio: formData.data,
          quantidadeSemanas: formData.quantidadeSemanas,
          consultaMensal: false
        }
      );
      
      console.log('[FormConsulta] Resultado recebido:', resultado);
      
      // CORREÇÃO: Verificação simplificada e mais robusta
      const consultasCriadas = resultado.consultasCriadas?.length || 0;
      const mensagemSucesso = resultado.mensagem?.toLowerCase().includes('sucesso') || false;
      const temSucesso = resultado.sucesso === true;
      
      // Se qualquer indicador de sucesso for verdadeiro E temos consultas criadas
      if ((temSucesso || mensagemSucesso) && consultasCriadas > 0) {
        alert(`${consultasCriadas} consultas recorrentes foram agendadas com sucesso!`);
        
        console.log('[FormConsulta] Sucesso confirmado, navegando para /consultas');
        
        // Navegação forçada e robusta
        try {
          navigate('/consultas');
        } catch (navError) {
          console.error('[FormConsulta] Erro na navegação, usando window.location');
          window.location.href = '/consultas';
        }
      } else {
        console.error('[FormConsulta] Falha na criação das consultas:', resultado);
        alert(`Erro: ${resultado.mensagem || 'Não foi possível criar as consultas recorrentes'}`);
      }
    } catch (error) {
      console.error('Erro ao criar consultas recorrentes:', error);
      
      let mensagemErro = 'Erro ao criar consultas recorrentes.';
      if (error instanceof Error) {
        mensagemErro += ' ' + error.message;
      }
      
      alert(mensagemErro);
    } finally {
      setSalvando(false);
    }
  };

  // Função de submit principal
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validação básica
    const novosErros: Record<string, string> = {};
    
    if (!formData.pacienteId) {
      novosErros.pacienteId = 'Selecione um paciente';
    }
    
    if (!formData.data) {
      novosErros.data = 'Selecione uma data';
    }
    
    if (!formData.hora) {
      novosErros.hora = 'Informe um horário';
    }
    
    const valorNumerico = parseFloat(String(formData.valor));
    if (isNaN(valorNumerico) || valorNumerico <= 0) {
      novosErros.valor = 'Informe um valor válido';
    }

    // Validação de hora fim > hora início
    if (formData.hora && formData.horaFim && formData.horaFim <= formData.hora) {
      novosErros.horaFim = 'Hora fim deve ser posterior à hora início';
    }

    // Validação específica para consultas recorrentes
    if (modoRecorrente) {
      if (!formData.diaDaSemana && formData.diaDaSemana !== 0) {
        novosErros.diaDaSemana = 'Selecione um dia da semana';
      }
      
      if (!formData.quantidadeSemanas || formData.quantidadeSemanas <= 0) {
        novosErros.quantidadeSemanas = 'Informe a quantidade de consultas';
      }
    }
    
    if (Object.keys(novosErros).length > 0) {
      setErros(novosErros);
      return;
    }

    // VALIDAÇÃO CRÍTICA: Verificar conflito de horário e disponibilidade
    if (validacaoAgendamento && !validacaoAgendamento.valido) {
      const tipoErro = validacaoAgendamento.tipo === 'conflito' 
        ? 'Conflito de Horário' 
        : 'Horário Indisponível';
      
      alert(`❌ ${tipoErro}\n\n${validacaoAgendamento.mensagem}\n\nPor favor, escolha outro horário.`);
      return;
    }
    
    // Se for modo recorrente, criar múltiplas consultas
    if (modoRecorrente && !id) {
      await criarConsultasRecorrentes();
      return;
    }
    
    // Consulta única (criação ou edição)
    try {
      setSalvando(true);
      
      if (id) {
        const consultaParaAPI: Partial<Consulta> = {
          dataConsulta: formData.data,
          horaInicio: formData.hora,
          horaFim: formData.horaFim || undefined,
          valor: typeof formData.valor === 'string' ? parseFloat(formData.valor) : formData.valor,
          statusConsulta: formData.statusConsulta,
          statusPagamento: formData.statusPagamento,
          dataPagamento: formData.dataPagamento || undefined,
          formaPagamento: formData.formaPagamento,
          observacoesAgendamento: formData.observacoesAgendamento,
          usuarioId: formData.usuarioId
        };
        
        await consultasService.atualizar(id, consultaParaAPI);
      } else {
        const consultaInput: ConsultaInput = {
          pacienteId: formData.pacienteId,
          usuarioId: formData.usuarioId,
          data: formData.data,
          hora: formData.hora,
          horaFim: formData.horaFim,
          valor: typeof formData.valor === 'string' ? parseFloat(formData.valor) : formData.valor,
          statusConsulta: formData.statusConsulta,
          statusPagamento: formData.statusPagamento,
          dataPagamento: formData.dataPagamento,
          formaPagamento: formData.formaPagamento,
          observacoes: formData.observacoesAgendamento
        };
        
        await consultasService.criar(consultaInput);
      }
      
      alert(`Consulta ${id ? 'atualizada' : 'agendada'} com sucesso!`);
      navigate('/consultas');
    } catch (error) {
      console.error('Erro ao salvar consulta:', error);
      alert(`Erro ao ${id ? 'atualizar' : 'agendar'} consulta. Tente novamente.`);
    } finally {
      setSalvando(false);
    }
  };
  
  const cancelarConsulta = async () => {
    if (!id) return;
    
    const confirmacao = window.confirm(
      'Tem certeza que deseja cancelar esta consulta?\n\n' +
      'Esta ação irá marcar a consulta como cancelada.'
    );
    
    if (!confirmacao) return;
    
    try {
      setSalvando(true);
      
      await consultasService.cancelar(id, 'Cancelada via formulário de edição');
      
      alert('Consulta cancelada com sucesso!');
      navigate('/consultas');
    } catch (error) {
      console.error('Erro ao cancelar consulta:', error);
      alert('Erro ao cancelar consulta. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  };

  const excluirConsultaDefinitivamente = async () => {
    if (!id) return;
    
    const confirmacao = window.confirm(
      'ATENÇÃO: Esta ação irá EXCLUIR DEFINITIVAMENTE a consulta do banco de dados.\n\n' +
      'Esta ação NÃO PODE SER DESFEITA!\n\n' +
      'Tem certeza que deseja continuar?'
    );
    
    if (!confirmacao) return;
    
    // Segunda confirmação para operações críticas
    const segundaConfirmacao = window.confirm(
      'ÚLTIMA CONFIRMAÇÃO:\n\n' +
      'Você está prestes a excluir PERMANENTEMENTE esta consulta.\n' +
      'Todos os dados serão perdidos para sempre.\n\n' +
      'Confirma a exclusão definitiva?'
    );
    
    if (!segundaConfirmacao) return;
    
    try {
      setSalvando(true);
      
      await consultasService.excluirDefinitivamente(id);
      
      alert('Consulta excluída definitivamente com sucesso!');
      navigate('/consultas');
    } catch (error) {
      console.error('Erro ao excluir consulta:', error);
      alert('Erro ao excluir consulta definitivamente. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  };
  
  // Função de verificação de conflitos removida (não utilizada neste fluxo)

  if (carregando) {
    return <div className="text-center my-5"><div className="spinner-border"></div><p>Carregando...</p></div>;
  }
  
  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-md-12">
          <div className="card card-primary card-outline">
            <div className="card-header">
              <div className="row align-items-center">
                <div className="col-md-8">
                  <h3 className="card-title h4">
                    <i className="fas fa-calendar-plus mr-2"></i>
                    {id ? 'Editar Consulta' : 'Nova Consulta'}
                  </h3>
                  {id && <small className="text-muted ml-3">ID: {id}</small>}
                </div>
                <div className="col-md-4 text-right">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/consultas')}
                    disabled={salvando}
                  >
                    <i className="fas fa-arrow-left mr-2"></i>
                    Voltar à Lista
                  </button>
                </div>
              </div>
            </div>

            <div className="card-body">
              <form onSubmit={handleSubmit}>
                {/* Passo 1: Seleção do Paciente */}
                <div className="card mb-4">
                  <div className="card-header bg-light">
                    <h5 className="card-title mb-0">
                      <i className="fas fa-user mr-2"></i>
                      Passo 1: Selecionar Paciente
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-12">
                        <PacienteSearchSelect
                          pacientes={todosPacientes}
                          value={formData.pacienteId}
                          onChange={handlePacienteChange}
                          placeholder="Digite para buscar ou clique para selecionar um paciente..."
                          error={erros.pacienteId}
                          required
                          label="Paciente"
                        />
                      </div>
                    </div>

                    {/* Link para detalhes do paciente */}
                    {pacienteSelecionado && (
                      <div className="mt-3 text-right">
                        <Link 
                          to={`/pacientes/${pacienteSelecionado.id}`}
                          className="btn btn-outline-info btn-sm"
                          target="_blank"
                        >
                          <i className="fas fa-external-link-alt mr-2"></i>
                          Ver Detalhes do Paciente
                        </Link>
                      </div>
                    )}
                  </div>
                </div>

                {/* Alerta de validação de agendamento */}
                {validacaoAgendamento && !validacaoAgendamento.valido && (
                  <div className={`alert ${validacaoAgendamento.tipo === 'conflito' ? 'alert-danger' : 'alert-warning'} mb-4`}>
                    <div className="d-flex align-items-center">
                      <i className={`fas ${validacaoAgendamento.tipo === 'conflito' ? 'fa-exclamation-circle' : 'fa-clock'} mr-3 fa-2x`}></i>
                      <div>
                        <h5 className="alert-heading mb-1">
                          {validacaoAgendamento.tipo === 'conflito' ? 'Conflito de Horário!' : 'Horário Indisponível'}
                        </h5>
                        <p className="mb-0">{validacaoAgendamento.mensagem}</p>
                        {validacaoAgendamento.tipo === 'conflito' && validacaoAgendamento.consultaConflitante && (
                          <small className="d-block mt-1">
                            <strong>Consulta existente:</strong> {validacaoAgendamento.consultaConflitante.pacienteNome} às {validacaoAgendamento.consultaConflitante.horaInicio}
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Indicador de validação em progresso */}
                {validando && (
                  <div className="alert alert-info mb-4">
                    <span className="spinner-border spinner-border-sm mr-2"></span>
                    Verificando disponibilidade...
                  </div>
                )}

                {/* Passo 2: Tipo de Agendamento (apenas para novas consultas) */}
                {!id && (
                  <div className="card mb-4">
                    <div className="card-header bg-light">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-calendar-alt mr-2"></i>
                        Passo 2: Tipo de Agendamento
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-6 mb-3 mb-md-0">
                          <div className="custom-control custom-radio">
                            <input
                              className="custom-control-input"
                              type="radio"
                              id="agendamentoUnico"
                              name="tipoAgendamento"
                              checked={!modoRecorrente}
                              onChange={() => setModoRecorrente(false)}
                            />
                            <label className="custom-control-label" htmlFor="agendamentoUnico">
                              <h6 className="mb-1">
                                <i className="fas fa-calendar-day mr-2"></i>
                                Consulta Única
                              </h6>
                              <small className="text-muted">
                                Agendar uma única consulta em data específica
                              </small>
                            </label>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="custom-control custom-radio">
                            <input
                              className="custom-control-input"
                              type="radio"
                              id="agendamentoRecorrente"
                              name="tipoAgendamento"
                              checked={modoRecorrente}
                              onChange={() => setModoRecorrente(true)}
                            />
                            <label className="custom-control-label" htmlFor="agendamentoRecorrente">
                              <h6 className="mb-1">
                                <i className="fas fa-calendar-week mr-2"></i>
                                Consultas Recorrentes
                              </h6>
                              <small className="text-muted">
                                Agendar múltiplas consultas semanais
                              </small>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Passo 3: Detalhes da Consulta */}
                <div className="card mb-4">
                  <div className="card-header bg-light">
                    <h5 className="card-title mb-0">
                      <i className="fas fa-clock mr-2"></i>
                      {id ? 'Passo 2' : 'Passo 3'}: Detalhes da Consulta
                    </h5>
                  </div>
                  <div className="card-body">
                    {/* Primeira linha: campos comuns */}
                    <div className="row">
                      <div className="col-md-3">
                        <div className="form-group">
                          <label htmlFor="data" className="form-label">
                            <i className="fas fa-calendar mr-2"></i>
                            {modoRecorrente ? 'Data de Referência *' : 'Data *'}
                          </label>
                          <input
                            type="date"
                            className={`form-control ${erros.data ? 'is-invalid' : ''}`}
                            id="data"
                            name="data"
                            value={formData.data}
                            onChange={handleChange}
                            required
                          />
                          {erros.data && <div className="invalid-feedback">{erros.data}</div>}
                          {modoRecorrente && (
                            <small className="form-text text-muted">
                              Data base para calcular as consultas recorrentes
                            </small>
                          )}
                        </div>
                      </div>
                      
                      <div className="col-md-3">
                        <div className="form-group">
                          <label htmlFor="hora" className="form-label">
                            <i className="fas fa-clock mr-2"></i>
                            Hora Início *
                          </label>
                          <select
                            className={`form-control ${erros.hora ? 'is-invalid' : ''}`}
                            id="hora"
                            name="hora"
                            value={formData.hora}
                            onChange={handleChange}
                            required
                          >
                            <option value="">Selecione...</option>
                            {horariosDisponiveis.map(h => (
                              <option key={h} value={h}>{h}</option>
                            ))}
                          </select>
                          {erros.hora && <div className="invalid-feedback">{erros.hora}</div>}
                        </div>
                      </div>
                      
                      <div className="col-md-3">
                        <div className="form-group">
                          <label htmlFor="horaFim" className="form-label">
                            <i className="fas fa-clock mr-2"></i>
                            Hora Fim
                          </label>
                          <select
                            className={`form-control ${formData.hora && formData.horaFim && formData.horaFim <= formData.hora ? 'is-invalid' : ''}`}
                            id="horaFim"
                            name="horaFim"
                            value={formData.horaFim || ''}
                            onChange={handleChange}
                            disabled={!formData.hora}
                          >
                            <option value="">Selecione...</option>
                            {horariosFimDisponiveis.map(h => (
                              <option key={h} value={h}>{h}</option>
                            ))}
                          </select>
                          <small className="form-text text-muted">
                            {!formData.hora ? 'Selecione hora início primeiro' : 'Opcional - apenas horários posteriores'}
                          </small>
                        </div>
                      </div>
                      
                      <div className="col-md-3">
                        <div className="form-group">
                          <label htmlFor="valor" className="form-label">
                            <i className="fas fa-dollar-sign mr-2"></i>
                            Valor *
                          </label>
                          <div className="input-group">
                            <div className="input-group-prepend">
                              <span className="input-group-text">R$</span>
                            </div>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              className={`form-control ${erros.valor ? 'is-invalid' : ''}`}
                              id="valor"
                              name="valor"
                              value={formData.valor}
                              onChange={handleChange}
                              required
                            />
                            {erros.valor && <div className="invalid-feedback">{erros.valor}</div>}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ✅ MULTI-PSICÓLOGOS: Psicólogo Responsável pela Consulta */}
                    <div className="row">
                      <div className="col-md-6">
                        <FiltroPsicologo
                          value={formData.usuarioId ? parseInt(formData.usuarioId as string) : null}
                          onChange={(psicologoId) => handleChange({ 
                            target: { name: 'usuarioId', value: psicologoId?.toString() || '' } 
                          } as any)}
                          incluirTodos={false}
                          label="Psicólogo Responsável *"
                        />
                        <small className="form-text text-muted">
                          <i className="fas fa-info-circle mr-1"></i>
                          Psicólogo que realizará a consulta
                        </small>
                      </div>
                    </div>

                    {/* Segunda linha: campos específicos para consultas recorrentes */}
                    {modoRecorrente && !id && (
                      <div className="row mt-3">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="diaDaSemana" className="form-label">
                              <i className="fas fa-calendar-day mr-2"></i>
                              Dia da Semana *
                            </label>
                            <select
                              className={`form-control ${erros.diaDaSemana ? 'is-invalid' : ''}`}
                              id="diaDaSemana"
                              name="diaDaSemana"
                              value={formData.diaDaSemana}
                              onChange={handleChange}
                              required
                            >
                              {DIAS_SEMANA.map((dia, index) => (
                                <option key={index} value={index}>
                                  {dia}
                                </option>
                              ))}
                            </select>
                            {erros.diaDaSemana && <div className="invalid-feedback">{erros.diaDaSemana}</div>}
                            <small className="form-text text-muted">
                              Todas as consultas serão agendadas neste dia da semana
                            </small>
                          </div>
                        </div>
                        
                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="quantidadeSemanas" className="form-label">
                              <i className="fas fa-list-ol mr-2"></i>
                              Quantidade de Consultas *
                            </label>
                            <input
                              type="number"
                              className={`form-control ${erros.quantidadeSemanas ? 'is-invalid' : ''}`}
                              id="quantidadeSemanas"
                              name="quantidadeSemanas"
                              min="1"
                              max="52"
                              value={formData.quantidadeSemanas}
                              onChange={handleChange}
                              required
                            />
                            {erros.quantidadeSemanas && <div className="invalid-feedback">{erros.quantidadeSemanas}</div>}
                            <small className="form-text text-muted">
                              Número de consultas a serem agendadas (1-52)
                            </small>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Terceira linha: observações */}
                    <div className="row mt-3">
                      <div className="col-md-12">
                        <div className="form-group">
                          <label htmlFor="observacoesAgendamento" className="form-label">
                            <i className="fas fa-sticky-note mr-2"></i>
                            Observações
                          </label>
                          <textarea
                            className="form-control"
                            id="observacoesAgendamento"
                            name="observacoesAgendamento"
                            rows={3}
                            value={formData.observacoesAgendamento || ''}
                            onChange={handleChange}
                            placeholder="Observações sobre o agendamento (opcional)..."
                          />
                        </div>
                      </div>
                    </div>

                    {/* Visualização das datas calculadas para consultas recorrentes */}
                    {modoRecorrente && !id && datasRecorrentes.length > 0 && (
                      <div className="mt-4">
                        <div className="alert alert-success">
                          <h6>
                            <i className="fas fa-calendar-check mr-2"></i>
                            Datas Calculadas para as Consultas Recorrentes
                          </h6>
                          <p className="mb-3">
                            Serão criadas <strong>{datasRecorrentes.length} consultas</strong> nas seguintes datas:
                          </p>
                          
                          <div className="table-responsive">
                            <table className="table table-sm table-bordered">
                              <thead className="thead-light">
                                <tr>
                                  <th style={{width: "10%"}}>#</th>
                                  <th style={{width: "25%"}}>Data</th>
                                  <th style={{width: "25%"}}>Dia da Semana</th>
                                  <th style={{width: "20%"}}>Horário</th>
                                  <th style={{width: "20%"}}>Valor</th>
                                </tr>
                              </thead>
                              <tbody>
                                {datasRecorrentes.map((data, index) => {
                                  const [ano, mes, dia] = data.split('-').map(Number);
                                  const dataObj = new Date(ano, mes - 1, dia, 12, 0, 0);
                                  const diaSemana = DIAS_SEMANA[dataObj.getDay()];
                                  
                                  return (
                                    <tr key={index}>
                                      <td>{index + 1}</td>
                                      <td>{data.split('-').reverse().join('/')}</td>
                                      <td>
                                        <span className="badge badge-info">{diaSemana}</span>
                                      </td>
                                      <td>
                                        {formData.hora}
                                        {formData.horaFim && ` - ${formData.horaFim}`}
                                      </td>
                                      <td>
                                        <strong className="text-success">
                                          R$ {parseFloat(String(formData.valor || 0)).toFixed(2)}
                                        </strong>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                              <tfoot>
                                <tr className="table-info">
                                  <td colSpan={4}>
                                    <strong>Total de Consultas: {datasRecorrentes.length}</strong>
                                  </td>
                                  <td>
                                    <strong className="text-success">
                                      R$ {(datasRecorrentes.length * parseFloat(String(formData.valor || 0))).toFixed(2)}
                                    </strong>
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Status da Consulta - Apenas para edição */}
                {id && (
                  <div className="card mb-4">
                    <div className="card-header bg-light">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-info-circle mr-2"></i>
                        Status e Pagamento
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-4">
                          <div className="form-group">
                            <label htmlFor="statusConsulta" className="form-label">
                              <i className="fas fa-calendar-check mr-2"></i>
                              Status da Consulta
                            </label>
                            <select
                              className="form-select"
                              id="statusConsulta"
                              name="statusConsulta"
                              value={formData.statusConsulta}
                              onChange={handleChange}
                            >
                              <option value="agendada">Agendada</option>
                              <option value="realizada">Realizada</option>
                              <option value="cancelada">Cancelada</option>
                              <option value="faltou">Paciente Faltou</option>
                            </select>
                          </div>
                        </div>
                        
                        <div className="col-md-4">
                          <div className="form-group">
                            <label htmlFor="statusPagamento" className="form-label">
                              <i className="fas fa-credit-card mr-2"></i>
                              Status do Pagamento
                            </label>
                            <select
                              className="form-select"
                              id="statusPagamento"
                              name="statusPagamento"
                              value={formData.statusPagamento}
                              onChange={handleChange}
                            >
                              <option value="pendente">Pendente</option>
                              <option value="pago">Pago</option>
                              <option value="cancelado">Cancelado</option>
                            </select>
                          </div>
                        </div>
                        
                        <div className="col-md-4">
                          {formData.statusPagamento === 'pago' && (
                            <div className="form-group">
                              <label htmlFor="dataPagamento" className="form-label">
                                <i className="fas fa-calendar-check mr-2"></i>
                                Data do Pagamento
                              </label>
                              <input
                                type="date"
                                className="form-control"
                                id="dataPagamento"
                                name="dataPagamento"
                                value={formData.dataPagamento || ''}
                                onChange={handleChange}
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {formData.statusPagamento === 'pago' && (
                        <div className="row">
                          <div className="col-md-6">
                            <div className="form-group">
                              <label htmlFor="formaPagamento" className="form-label">
                                <i className="fas fa-money-bill mr-2"></i>
                                Forma de Pagamento
                              </label>
                              <select
                                className="form-select"
                                id="formaPagamento"
                                name="formaPagamento"
                                value={formData.formaPagamento || ''}
                                onChange={handleChange}
                              >
                                <option value="">Selecione...</option>
                                <option value="dinheiro">💵 Dinheiro</option>
                                <option value="pix">📱 PIX</option>
                                <option value="cartao">💳 Cartão</option>
                                <option value="transferencia">🏦 Transferência</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Botões de Ação - Desktop/Tablet */}
                <div className="card d-none d-md-block">
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        {id && (
                          <div className="btn-group" role="group">
                            <button
                              type="button"
                              className="btn btn-warning"
                              onClick={cancelarConsulta}
                              disabled={salvando || formData.statusConsulta === 'cancelada'}
                              title="Cancelar consulta"
                            >
                              {salvando ? (
                                <>
                                  <span className="spinner-border spinner-border-sm mr-2"></span>
                                  Cancelando...
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-ban mr-2"></i>
                                  Cancelar Consulta
                                </>
                              )}
                            </button>

                            <button
                              type="button"
                              className="btn btn-danger"
                              onClick={excluirConsultaDefinitivamente}
                              disabled={salvando}
                              title="Excluir definitivamente"
                            >
                              {salvando ? (
                                <>
                                  <span className="spinner-border spinner-border-sm mr-2"></span>
                                  Excluindo...
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-trash mr-2"></i>
                                  Excluir
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                      
                      <div className="col-md-6 text-right">
                        <div className="btn-group" role="group">
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => navigate('/consultas')}
                            disabled={salvando}
                          >
                            <i className="fas fa-times mr-2"></i>
                            Cancelar
                          </button>
                          
                          <button
                            type="submit"
                            className={`btn ${validacaoAgendamento && !validacaoAgendamento.valido ? 'btn-danger' : 'btn-primary'} btn-lg`}
                            disabled={salvando || !formData.pacienteId || !formData.data || !formData.hora || !formData.valor || validando || !!(validacaoAgendamento && !validacaoAgendamento.valido)}
                            title={validacaoAgendamento && !validacaoAgendamento.valido ? validacaoAgendamento.mensagem : undefined}
                          >
                            {salvando ? (
                              <>
                                <span className="spinner-border spinner-border-sm mr-2"></span>
                                {modoRecorrente ? 'Criando consultas...' : 'Salvando...'}
                              </>
                            ) : validando ? (
                              <>
                                <span className="spinner-border spinner-border-sm mr-2"></span>
                                Verificando...
                              </>
                            ) : validacaoAgendamento && !validacaoAgendamento.valido ? (
                              <>
                                <i className="fas fa-exclamation-triangle mr-2"></i>
                                {validacaoAgendamento.tipo === 'conflito' ? 'Conflito de Horário' : 'Horário Indisponível'}
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save mr-2"></i>
                                {modoRecorrente ? `Criar ${datasRecorrentes.length} Consultas` : (id ? 'Atualizar' : 'Salvar')}
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Botões de Ação - Mobile (barra fixa) */}
                <div className="mobile-action-bar d-md-none">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate('/consultas')}
                    disabled={salvando}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className={`btn ${validacaoAgendamento && !validacaoAgendamento.valido ? 'btn-danger' : 'btn-primary'}`}
                    disabled={salvando || !formData.pacienteId || !formData.data || !formData.hora || !formData.valor || validando || !!(validacaoAgendamento && !validacaoAgendamento.valido)}
                  >
                    {salvando ? 'Salvando...' : validando ? 'Verificando...' : validacaoAgendamento && !validacaoAgendamento.valido ? 'Horário Inválido' : (modoRecorrente ? `Criar ${datasRecorrentes.length} Consultas` : (id ? 'Atualizar' : 'Salvar'))}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
